import { useMemo, useState } from 'react';
import { UseQueryOptions } from 'react-query';
import { useAPI } from '@/services/utils';
import { useGetFundCodeFromQuery } from '@/modules/invest/hooks/useGetFundCodeFromQuery';
import {
  Dayjs,
  dayjsToUnix,
  findMaxDate,
  getDate,
  getSubstractDate,
} from '@/utils/date';

// since these hooks have a similar situation, then we decided to keep them in 1 file together.

export const useGetTotalAssetTrend = ({
  fromMonthAgo,
  reactQueryOptions,
}: {
  fromMonthAgo?: number;
  reactQueryOptions?: Omit<UseQueryOptions<any>, 'onSuccess'>;
}) => {
  const [firstTransactionDate, setFirstTransactionDate] =
    useState<Dayjs | null>(null);

  const startDate = useMemo(() => {
    if (fromMonthAgo) {
      const fromDateAgo = getSubstractDate(fromMonthAgo, 'month');
      if (firstTransactionDate) {
        return findMaxDate(firstTransactionDate, fromDateAgo);
      }
      return fromDateAgo;
    }
    return firstTransactionDate || getDate();
  }, [fromMonthAgo, firstTransactionDate]);

  return useAPI(['fund', 'getTotalAssetTrend'], {
    input: { StartDate: dayjsToUnix(startDate), EndDate: dayjsToUnix() },
    reactQueryOptions: {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: !firstTransactionDate ? 0 : undefined,
      ...reactQueryOptions,
      onSuccess: ({ firstTransactionDate: startDate }) => {
        if (!firstTransactionDate && startDate) {
          setFirstTransactionDate(getDate(startDate));
        }
      },
    },
  });
};

export const useGetAssetTrend = ({
  fundCode,
  fromMonthAgo,
  reactQueryOptions,
}: {
  fundCode: string;
  fromMonthAgo?: number;
  reactQueryOptions?: Omit<UseQueryOptions<any>, 'onSuccess'>;
}) => {
  const [firstTransactionDate, setFirstTransactionDate] =
    useState<Dayjs | null>(null);

  const startDate = useMemo(() => {
    if (fromMonthAgo) {
      const fromDateAgo = getSubstractDate(fromMonthAgo, 'month');
      if (firstTransactionDate) {
        return findMaxDate(firstTransactionDate, fromDateAgo);
      }
      return fromDateAgo;
    }
    return firstTransactionDate || getDate();
  }, [fromMonthAgo, firstTransactionDate]);

  return useAPI(['fund', 'getAssetTrend'], {
    input: {
      fundCode,
      startDate: dayjsToUnix(startDate),
      endDate: dayjsToUnix(),
    },
    reactQueryOptions: {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: !firstTransactionDate ? 0 : undefined,
      ...reactQueryOptions,
      onSuccess: ({ assetTrendTotal }) => {
        if (!firstTransactionDate && assetTrendTotal?.firstTransactionDate) {
          setFirstTransactionDate(
            getDate(assetTrendTotal.firstTransactionDate)
          );
        }
      },
    },
  });
};

export const useGetPriceHistory = ({
  fundCode,
  fromMonthAgo,
  reactQueryOptions,
}: {
  fundCode: string;
  fromMonthAgo?: number;
  reactQueryOptions?: Omit<UseQueryOptions<any>, 'select'>;
}) => {
  const startDate = useMemo(() => {
    if (fromMonthAgo) {
      return getSubstractDate(fromMonthAgo, 'month');
    }
    return null;
  }, [fromMonthAgo]);

  const GET_HISTORY_PAGE_SIZE = 10000;
  const GET_HISTORY_PAGE_NUMBER = 1;

  return useAPI(['fund', 'getHistory'], {
    input: {
      fundCode,
      startDate: startDate ? dayjsToUnix(startDate) : 0,
      endDate: dayjsToUnix(),
      pageSize: GET_HISTORY_PAGE_SIZE,
      pageNo: GET_HISTORY_PAGE_NUMBER,
    },
    reactQueryOptions: {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      ...reactQueryOptions,
      select: (data) => {
        if (startDate) {
          return data;
        }
        return {
          ...data,
          items: (data?.items || [])?.filter((v) => v.redemptionNav > 0),
        };
      },
    },
  });
};

export const useGetETFHistory = ({
  fromMonthAgo,
  reactQueryOptions,
}: {
  fromMonthAgo?: number;
  reactQueryOptions?: UseQueryOptions<any>;
}) => {
  const API_INPUT_DATE_FORMAT = 'YYYY-MM-DD';
  const Isin = useGetFundCodeFromQuery();

  const ToDate = getDate().format(API_INPUT_DATE_FORMAT);
  const FromDate = fromMonthAgo
    ? getSubstractDate(fromMonthAgo, 'month').format(API_INPUT_DATE_FORMAT)
    : undefined;

  return useAPI(['fund', 'getStockHistory'], {
    input: { Isin, ToDate, ...{ FromDate } },
    reactQueryOptions: {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      ...reactQueryOptions,
      // select: (data) => {
      //   return {
      //     ...data,
      //     items: (data?.items || [])?.filter((v) => Object.keys(v).length > 1),
      //   };
      // },
    },
  });
};
export const useGetStockAssetTrend = ({
  fromMonthAgo,
  reactQueryOptions,
}: {
  fromMonthAgo?: number;
  reactQueryOptions?: UseQueryOptions<any>;
}) => {
  const [firstTransactionDate, setFirstTransactionDate] =
    useState<Dayjs | null>(null);

  const Isin = useGetFundCodeFromQuery();

  const FromDate = useMemo(() => {
    if (fromMonthAgo) {
      const fromDateAgo = getSubstractDate(fromMonthAgo, 'month');
      if (firstTransactionDate) {
        return findMaxDate(firstTransactionDate, fromDateAgo);
      }
      return fromDateAgo;
    }
    return firstTransactionDate || getDate();
  }, [fromMonthAgo, firstTransactionDate]);

  return useAPI(['trade', 'getStockAssetTrend'], {
    input: {
      Isin,
      ToDate: dayjsToUnix(),
      FromDate: dayjsToUnix(FromDate),
    },
    reactQueryOptions: {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: !firstTransactionDate ? 0 : undefined,
      ...reactQueryOptions,
      onSuccess: ({ firstTransaction }) => {
        if (!firstTransactionDate && firstTransaction) {
          setFirstTransactionDate(getDate(firstTransaction));
        }
      },
    },
  });
};
